import { useT } from '@/store';
import { useQuote } from '@/hooks/useAnalytics';
import { eurExact } from '@/lib/format';

/**
 * What this load keeps, while the rate is still on the table.
 *
 * Everything this product knows about the money on a load, it has known one
 * delivery too late. The route matrix will tell a carrier that a run at
 * 8,00 €/t keeps 0,42 €/t — after they have quoted it that way forty times.
 * This is the same arithmetic, in the moment it can still change the answer.
 *
 * WHAT IT MUST NOT DO is look more certain than it is, and there are three
 * separate ways it could:
 *
 *  - BY HIDING A MISSING COST LINE. A carrier who has never entered a
 *    driver rate would otherwise see a margin that is simply the lines this
 *    product happens to know about — and it looks BETTER than the truth,
 *    which is the direction that gets a run quoted too cheaply. So every
 *    absent line is named, with the reason, under the figure.
 *
 *  - BY HIDING THE EMPTY LEG. Repositioning is measured from the driver's
 *    PREVIOUS delivery, which has not happened yet and cannot be invented.
 *    Every quote is therefore optimistic by the cost of the run home, and
 *    that line is on every answer rather than only the incomplete ones.
 *
 *  - BY DRESSING AN ASSUMPTION AS A MEASUREMENT. A straight-line distance
 *    and schema.sql's 0,35 €/km default both produce a perfectly plausible
 *    number. The provenance travels with the figure and is shown.
 *
 * The panel renders nothing at all until the form can actually be quoted —
 * a route, a material and a tonnage. A dispatcher half-way through typing
 * is the ordinary state of a form, not an error to report.
 */

const EM_DASH = '—';

/** A cost line absent from the margin -> the sentence explaining it. Keyed
 *  by the engine's own reason strings, so the vocabulary has one author. */
const REASONS = {
  no_distance: 'quote.missingNoDistance',
  not_configured: 'quote.missingNotConfigured',
  no_route_history: 'quote.missingNoRouteHistory',
  not_priced: 'quote.missingNotPriced',
  no_weight: 'quote.missingNoWeight',
  unknowable_before_dispatch: 'quote.missingEmptyLeg',
};

export function QuotePreview({ quarryId, siteId, cargoType, tons, pricePerTon }) {
  const t = useT();
  const { data, isLoading, isError } = useQuote({
    quarryId, siteId, cargoType, tons, pricePerTon,
  });

  if (!data && !isLoading && !isError) return null;

  return (
    <div className="rounded-[10px] border border-line bg-white/[0.02] px-3 py-2.5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="text-[10.5px] font-bold uppercase tracking-[0.03em] text-dim">
          {t('quote.title')}
        </span>
        {isLoading ? (
          <span className="text-[11.5px] text-dim">{t('common.loading')}</span>
        ) : null}
        {isError ? (
          <span className="text-[11.5px] text-dim">{t('quote.unavailable')}</span>
        ) : null}
      </div>

      {data ? (
        <>
          <div className="mt-1.5 flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <span className="text-[11.5px] text-dim">
              {t('quote.revenue')}{' '}
              <span className="tabular-nums text-ink">
                {data.revenue_eur == null ? EM_DASH : eurExact(data.revenue_eur)}
              </span>
            </span>
            <span className="text-[11.5px] text-dim">
              {t('quote.margin')}{' '}
              {/* Negative in red and bold: a run quoted below cost is the
                  one thing on this panel that has to stop a dispatcher. */}
              <span
                className={`text-[15px] font-bold tabular-nums ${
                  data.net_margin_eur != null && data.net_margin_eur < 0
                    ? 'text-[#FCA5A5]'
                    : 'text-ink'
                }`}
              >
                {data.net_margin_eur == null ? EM_DASH : eurExact(data.net_margin_eur)}
              </span>
            </span>
            {data.margin_per_ton_eur != null ? (
              <span className="text-[11.5px] text-dim">
                {t('quote.perTon')}{' '}
                <span className="tabular-nums text-ink">{eurExact(data.margin_per_ton_eur)}</span>
              </span>
            ) : null}
            {data.margin_pct != null ? (
              <span className="text-[11.5px] text-dim">
                {t('quote.share')}{' '}
                <span className="tabular-nums text-ink">{data.margin_pct.toFixed(0)}%</span>
              </span>
            ) : null}
          </div>

          {/* Where the two assumptions under the fuel line came from. A
              straight-line distance understates real road distance by
              30–45 %, and an unconfirmed €/km is schema.sql's default
              rather than anything this carrier measured — both produce a
              plausible number, so the provenance has to be visible. */}
          <p className="mt-1.5 text-[10px] leading-snug text-dim">
            {t('quote.basis', {
              distance: t(data.distance_source === 'routed'
                ? 'quote.distanceRouted' : 'quote.distanceEstimated'),
              rate: t(data.fuel_rate_source === 'measured'
                ? 'quote.rateMeasured' : 'quote.rateAssumed'),
            })}
            {data.cycle_sample_n
              ? ` · ${t('quote.cycleFrom', { n: data.cycle_sample_n })}`
              : ''}
          </p>

          {data.missing?.length ? (
            <ul className="mt-1 space-y-0.5">
              {data.missing.map((m) => (
                <li key={m.line} className="text-[10px] leading-snug text-amber">
                  {t(REASONS[m.reason] ?? 'quote.missingUnknown', {
                    line: t(`manager.cost_${m.line}`),
                  })}
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
